import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/api/client';
import { CheckCircle2, XCircle } from 'lucide-react';

type UnlockApiResponse = {
  success: boolean;
  message: string;
};

export default function UnlockPage() {
  const { code } = useParams<{ code: string }>();
  const { t } = useTranslation();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const calledRef = useRef(false);

  useEffect(() => {
    if (!code || calledRef.current) return;
    calledRef.current = true;

    apiClient<UnlockApiResponse>(`/unlock/${code}`, {
      method: 'GET',
      skipAuth: true,
    })
      .then((result) => {
        setStatus(result.success ? 'success' : 'error');
        setMessage(result.message);
      })
      .catch((err) => {
        setStatus('error');
        setMessage(err.message || 'Failed to unlock. Please use your access code.');
      });
  }, [code]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="max-w-sm space-y-6 text-center">
        {status === 'loading' && (
          <>
            <div className="mx-auto h-16 w-16 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-lg font-medium">{t('booking.unlocking')}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 className="h-12 w-12 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold">{t('booking.door_unlocked')}</h1>
            <p className="text-muted-foreground">
              {message.includes('Head to ')
                ? t('booking.door_unlocked_message', { unit: message.replace('Door unlocked! Head to ', '') })
                : message}
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
              <XCircle className="h-12 w-12 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold">{t('booking.unlock_failed')}</h1>
            <p className="text-muted-foreground">
              {message.includes('early') ? t('booking.unlock_error_early')
                : message.includes('ended') ? t('booking.unlock_error_ended')
                : message}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('booking.unlock_fallback')}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
